/* Client-scoped state for the measured field-extraction pass (W2-E).
 *
 * Extraction is an EXPLICIT AM action, never ambient: nothing here fires on
 * page-load. `runExtraction` is called from a button; both the property drawer
 * and the Overview "What needs improving" conflicts group read the resulting
 * store. State is reset on cockpit teardown so one client's extraction never
 * bleeds into another.
 */
import { createStore, type Unsubscribe } from './store.ts';
import { extractProperties, type ExtractResponse, type FieldConflict, type PageFieldRecord } from '../api/pageFields.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { NormalizedAsset } from '../types/contract.ts';

export type ExtractionStatus = 'idle' | 'loading' | 'ok' | 'error';

export interface ExtractionState {
  slug: string | null;
  status: ExtractionStatus;
  records: PageFieldRecord[];
  conflicts: FieldConflict[];
  coverage: ExtractResponse['coverage'] | null;
  reason: string | null;
}

const IDLE: ExtractionState = {
  slug: null,
  status: 'idle',
  records: [],
  conflicts: [],
  coverage: null,
  reason: null,
};

const store = createStore<ExtractionState>({ ...IDLE });

export function extractionState(): ExtractionState {
  return store.get();
}

export function subscribeExtraction(fn: (s: ExtractionState) => void): Unsubscribe {
  return store.subscribe(fn);
}

export function resetExtraction(): void {
  store.set({ ...IDLE });
}

/** A stable key for matching a property URL to its extracted record — scheme +
 * `www.` + trailing slash insensitive, so an asset's stored URL matches the
 * echoed propertyUrl regardless of cosmetic differences. */
export function propertyKey(url: string | null | undefined): string {
  if (!url) return '';
  let s = url.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
  return s;
}

/** The extracted record for a property URL, or null if this run did not cover it. */
export function recordForUrl(url: string | null | undefined): PageFieldRecord | null {
  const key = propertyKey(url);
  if (!key) return null;
  return store.get().records.find((r) => propertyKey(r.propertyUrl) === key) ?? null;
}

/** The client's extractable property URLs: real owned/editable/claimable/media
 * assets with an http(s) URL, one per host, capped. Platform profiles that block
 * unauthenticated fetches are still included — the connector reports them as
 * honest "unverifiable", which is itself a measured outcome. */
export function extractableUrls(state: AppPayloadState, cap = 8): string[] {
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const a of state.assets as NormalizedAsset[]) {
    if (!a.url) continue;
    let host: string;
    try {
      const u = new URL(a.url);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') continue;
      host = u.hostname.toLowerCase();
    } catch {
      continue;
    }
    if (seen.has(host)) continue;
    seen.add(host);
    urls.push(a.url);
    if (urls.length >= cap) break;
  }
  return urls;
}

/** Run the extraction pass for a client's properties. Idempotent while in
 * flight for the same slug; notifies store subscribers on settle. */
export async function runExtraction(slug: string, urls: string[]): Promise<void> {
  const cur = store.get();
  if (cur.slug === slug && cur.status === 'loading') return;
  store.set({ slug, status: 'loading', records: [], conflicts: [], coverage: null, reason: null });
  try {
    const resp = await extractProperties(slug, urls);
    // A newer run for a different client supersedes this response.
    if (store.get().slug !== slug) return;
    store.set({
      slug,
      status: 'ok',
      records: resp.records || [],
      conflicts: resp.conflicts || [],
      coverage: resp.coverage || null,
      reason: null,
    });
  } catch (err) {
    if (store.get().slug !== slug) return;
    store.set({
      slug,
      status: 'error',
      records: [],
      conflicts: [],
      coverage: null,
      reason: err instanceof Error ? err.message : 'Field extraction request failed.',
    });
  }
}
